import { defineMessages, IntlShape } from 'react-intl'

import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import type { CoordinatedCareThreadSection } from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/messages/CoordinatedCareThreads'

import { MessengerParticipant, MessengerThread, PARTICIPANT_CONFIG } from './types'

const roleMessages = defineMessages({
  client: { defaultMessage: 'Client', description: 'Client thread subtitle' },
  caregiver: { defaultMessage: 'Caregiver conversation', description: 'Caregiver thread subtitle' },
})

const getUnreadCount = (thread: MessengerThread) =>
  PARTICIPANT_CONFIG[thread.participant].showUnreadBadge ? thread.unreadCount : undefined

export const buildCoordinatedCareThreadSections = (
  threads: MessengerThread[],
  intl: IntlShape,
): CoordinatedCareThreadSection[] => {
  const clientThreads = threads.filter(({ participant }) => participant !== MessengerParticipant.PROVIDER_PAIR)
  const providerThreads = threads.filter(({ participant }) => participant === MessengerParticipant.PROVIDER_PAIR)
  return [
    {
      header: clientThreads.some(({ participant }) => participant === MessengerParticipant.GUARDIAN)
        ? 'client-caregiver'
        : 'client',
      threads: clientThreads.map((thread) => ({
        id: thread.id,
        topology: 'client',
        participantNames: [thread.recipientName || thread.tabLabel],
        subtitle:
          thread.participant === MessengerParticipant.AI
            ? thread.tabLabel
            : intl.formatMessage(
                roleMessages[thread.participant === MessengerParticipant.GUARDIAN ? 'caregiver' : 'client'],
              ),
        isReadOnly: !thread.canSendMessages,
        unreadCount: getUnreadCount(thread),
      })),
    },
    {
      header: 'provider-team',
      threads: providerThreads.map((thread) => ({
        id: thread.id,
        topology: 'provider-pair',
        participantNames: [thread.recipientName],
        subtitle:
          [
            ...new Set(
              thread.providerPairProgramNames?.map((name) => getProgramDisplayLabel(intl, name)).filter(Boolean),
            ),
          ].join(', ') ||
          intl.formatMessage({
            defaultMessage: 'Provider conversation',
            description: 'Provider-pair subtitle when associated program labels are unavailable',
          }),
        providerStatus: thread.providerPairStatus,
        unreadCount: getUnreadCount(thread),
        isReadOnly: !thread.canSendMessages,
      })),
    },
  ]
}
